import {
  useCreate,
  useNotify,
  useTranslate,
  useUpdate,
  type Identifier,
} from "ra-core";
import { useEffect, useId, useState, type FormEvent } from "react";

import { toIsoDay } from "@/components/market-intel/dates";
import {
  DateField,
  Field,
  FieldError,
  FormDialog,
  SELECT_CLASS,
} from "@/components/market-intel/FormDialog";
import {
  COMPETITOR_LINES,
  lineLabelKey,
} from "@/components/market-intel/labels";
import type {
  CompetitorLine,
  MarketCompetitor,
} from "@/components/market-intel/types";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useIsMobile } from "@/hooks/use-mobile";

import {
  nextPosition,
  normalizeSwotAnalysisForm,
  normalizeSwotItemForm,
  swotAnalysisFormDefaults,
  SWOT_QUADRANTS,
  swotQuadrantLabelKey,
  type SwotAnalysisFormValues,
  type SwotItemFormValues,
} from "./swot";
import type { MarketSwotAnalysis, MarketSwotItem, SwotQuadrant } from "./types";
import { FormActions } from "./ui";

export const SwotAnalysisForm = ({
  open,
  onOpenChange,
  analysis,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  analysis: MarketSwotAnalysis | null;
  onCreated?: (analysis: MarketSwotAnalysis) => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const isMobile = useIsMobile();
  const formId = useId();
  const today = toIsoDay(new Date());
  const [values, setValues] = useState(() => swotAnalysisFormDefaults(today));
  const [error, setError] = useState<string | null>(null);
  const [create, { isPending: creating }] = useCreate<MarketSwotAnalysis>();
  const [update, { isPending: updating }] = useUpdate<MarketSwotAnalysis>();

  useEffect(() => {
    if (open) {
      setValues(swotAnalysisFormDefaults(today, analysis ?? undefined));
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, analysis]);

  const set =
    <K extends keyof SwotAnalysisFormValues>(key: K) =>
    (value: SwotAnalysisFormValues[K]) => {
      setValues((current) => ({ ...current, [key]: value }));
      setError(null);
    };
  const close = () => onOpenChange(false);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const result = normalizeSwotAnalysisForm(values);
    if (!result.ok) return setError(result.error);
    const onError = () => notify("competition.form.error", { type: "error" });
    if (analysis) {
      update(
        "market_swot_analyses",
        { id: analysis.id, data: result.data, previousData: analysis },
        {
          onSuccess: () => {
            notify("competition.form.saved", { type: "success" });
            close();
          },
          onError,
        },
      );
      return;
    }
    create(
      "market_swot_analyses",
      { data: result.data },
      {
        onSuccess: (created) => {
          notify("competition.form.saved", { type: "success" });
          close();
          onCreated?.(created);
        },
        onError,
      },
    );
  };

  const errorId = `${formId}-error`;
  const text = (
    key: "title" | "segment" | "source" | "source_url",
    props: { placeholder?: string; type?: string } = {},
  ) => (
    <Field
      id={`${formId}-${key}`}
      label={translate(`competition.swot.form.${key}`)}
    >
      <Input
        id={`${formId}-${key}`}
        value={values[key]}
        onChange={(event) => set(key)(event.target.value)}
        {...props}
        {...(key === "title"
          ? {
              autoFocus: !isMobile,
              "aria-invalid":
                error === "competition.form.title_required" || undefined,
              "aria-describedby": error ? errorId : undefined,
            }
          : {})}
      />
    </Field>
  );
  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={translate(
        analysis ? "competition.swot.edit" : "competition.swot.add",
      )}
      formId={formId}
      onSubmit={submit}
      actions={
        <FormActions
          formId={formId}
          disabled={creating || updating}
          onCancel={close}
        />
      }
    >
      {text("title")}
      {error ? <FieldError id={errorId}>{translate(error)}</FieldError> : null}
      {text("segment", {
        placeholder: translate("competition.swot.form.segment_placeholder"),
      })}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field
          id={`${formId}-line`}
          label={translate("competition.swot.form.line")}
        >
          <select
            id={`${formId}-line`}
            className={SELECT_CLASS}
            value={values.line}
            onChange={(event) =>
              set("line")(event.target.value as CompetitorLine | "")
            }
          >
            <option value="">
              {translate("competition.swot.form.no_line")}
            </option>
            {COMPETITOR_LINES.map((line) => (
              <option key={line} value={line}>
                {translate(lineLabelKey(line))}
              </option>
            ))}
          </select>
        </Field>
        <Field
          id={`${formId}-analysis_date`}
          label={translate("competition.swot.form.analysis_date")}
        >
          <DateField
            id={`${formId}-analysis_date`}
            value={values.analysis_date}
            onChange={(event) => set("analysis_date")(event.target.value)}
          />
        </Field>
      </div>
      {text("source")}
      {text("source_url", { type: "url" })}
      <Field
        id={`${formId}-summary`}
        label={translate("competition.swot.form.summary")}
      >
        <Textarea
          id={`${formId}-summary`}
          value={values.summary}
          onChange={(event) => set("summary")(event.target.value)}
          rows={3}
        />
      </Field>
    </FormDialog>
  );
};

export const SwotItemForm = ({
  open,
  onOpenChange,
  analysisId,
  item,
  quadrant,
  items,
  competitors,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  analysisId: Identifier;
  item: MarketSwotItem | null;
  /** Quadrant of a new item (the one whose "+" was tapped). */
  quadrant: SwotQuadrant;
  items: readonly MarketSwotItem[];
  competitors: readonly MarketCompetitor[];
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const isMobile = useIsMobile();
  const formId = useId();
  const blank = (): SwotItemFormValues => ({
    quadrant: item?.quadrant ?? quadrant,
    content: item?.content ?? "",
    competitor_id:
      item?.competitor_id != null ? String(item.competitor_id) : "",
  });
  const [values, setValues] = useState(blank);
  const [error, setError] = useState<string | null>(null);
  const [create, { isPending: creating }] = useCreate();
  const [update, { isPending: updating }] = useUpdate();

  useEffect(() => {
    if (open) {
      setValues(blank());
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item, quadrant]);

  const set =
    <K extends keyof SwotItemFormValues>(key: K) =>
    (value: SwotItemFormValues[K]) => {
      setValues((current) => ({ ...current, [key]: value }));
      setError(null);
    };
  const close = () => onOpenChange(false);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const result = normalizeSwotItemForm(
      analysisId,
      values,
      competitors.map((competitor) => competitor.id),
    );
    if (!result.ok) return setError(result.error);
    const options = {
      onSuccess: () => {
        notify("competition.form.saved", { type: "success" });
        close();
      },
      onError: () => notify("competition.form.error", { type: "error" }),
    };
    const position = nextPosition(
      items.filter((entry) => entry.quadrant === result.data.quadrant),
    );
    if (item)
      update(
        "market_swot_items",
        {
          id: item.id,
          data:
            item.quadrant === result.data.quadrant
              ? result.data
              : { ...result.data, position },
          previousData: item,
        },
        options,
      );
    else
      create(
        "market_swot_items",
        { data: { ...result.data, position } },
        options,
      );
  };

  const errorId = `${formId}-error`;
  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={translate(
        item ? "competition.swot.edit_item" : "competition.swot.add_item",
      )}
      formId={formId}
      onSubmit={submit}
      actions={
        <FormActions
          formId={formId}
          disabled={creating || updating}
          onCancel={close}
        />
      }
    >
      <Field
        id={`${formId}-quadrant`}
        label={translate("competition.swot.quadrants.label")}
      >
        <select
          id={`${formId}-quadrant`}
          className={SELECT_CLASS}
          value={values.quadrant}
          onChange={(event) =>
            set("quadrant")(event.target.value as SwotQuadrant)
          }
        >
          {SWOT_QUADRANTS.map((entry) => (
            <option key={entry} value={entry}>
              {translate(swotQuadrantLabelKey(entry))}
            </option>
          ))}
        </select>
      </Field>
      <Field
        id={`${formId}-content`}
        label={translate("competition.swot.item_form.content")}
      >
        <Textarea
          id={`${formId}-content`}
          value={values.content}
          onChange={(event) => set("content")(event.target.value)}
          rows={3}
          autoFocus={!isMobile}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
        />
      </Field>
      {error ? <FieldError id={errorId}>{translate(error)}</FieldError> : null}
      <Field
        id={`${formId}-competitor`}
        label={translate("competition.swot.item_form.competitor")}
      >
        <select
          id={`${formId}-competitor`}
          className={SELECT_CLASS}
          value={values.competitor_id}
          onChange={(event) => set("competitor_id")(event.target.value)}
        >
          <option value="">{translate("competition.form.none")}</option>
          {competitors.map((competitor) => (
            <option key={competitor.id} value={String(competitor.id)}>
              {competitor.name}
            </option>
          ))}
        </select>
      </Field>
    </FormDialog>
  );
};
